/**
 * TakeItBanner - Animated non-blocking banner for high-confidence signals
 *
 * Shows an animated "TAKE IT" message when confidence + confluence exceeds threshold
 * Non-interfering: user can still interact with the app behind it
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './TakeItBanner.css';

interface TakeItBannerProps {
  isVisible: boolean;
  direction: 'LONG' | 'SHORT' | null;
  confidence: number;
  confluence: number;
  symbol?: string;
  onDismiss: () => void;
  onSymbolClick?: (symbol: string) => void; // Called when user clicks symbol to switch chart
  autoDismissMs?: number; // Auto-dismiss after this many ms (default: 10000)
}

// Helper to get short symbol name (MNQ, NQ, MES, ES)
const getShortSymbol = (symbol: string): string => {
  if (!symbol) return '';
  // Remove contract month and exchange suffix (e.g., MNQH6.CME -> MNQ)
  const match = symbol.match(/^(MNQ|NQ|MES|ES|M?CL|M?GC|M?SI)/i);
  return match ? match[1].toUpperCase() : symbol.split(/[0-9.]/)[0].toUpperCase();
};

export const TakeItBanner: React.FC<TakeItBannerProps> = ({
  isVisible,
  direction,
  confidence,
  confluence,
  symbol = '',
  onDismiss,
  onSymbolClick,
  autoDismissMs = 10000,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Use ref to avoid effect re-triggering when onDismiss changes
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const handleDismiss = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => onDismissRef.current(), 300); // Wait for exit animation
  }, []);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      const seconds = Math.floor(autoDismissMs / 1000);
      setCountdown(seconds);

      // Countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-dismiss after delay
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => onDismissRef.current(), 300);
      }, autoDismissMs);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [isVisible, autoDismissMs]);

  if (!isVisible && !isAnimating) return null;

  const combinedScore = Math.round(confidence + confluence);
  const isLong = direction === 'LONG';
  const shortSymbol = getShortSymbol(symbol);

  return createPortal(
    <div className={`take-it-banner-container ${isAnimating ? 'visible' : 'hidden'}`}>
      <div className={`take-it-banner ${isLong ? 'long' : 'short'}`}>
        <button className="take-it-close" onClick={handleDismiss} title="Dismiss">
          <X size={20} />
        </button>

        <div className="take-it-content">
          <div className="take-it-emoji">💰💲</div>
          <div className="take-it-text">TAKE IT</div>
          <div className={`take-it-direction ${isLong ? 'long' : 'short'}`}>
            {shortSymbol && (
              <span
                className={`take-it-symbol ${onSymbolClick ? 'clickable' : ''}`}
                onClick={() => onSymbolClick && symbol && onSymbolClick(symbol)}
                title={onSymbolClick ? `Switch to ${shortSymbol} chart` : undefined}
              >
                {shortSymbol}
              </span>
            )}
            {direction}
          </div>
        </div>

        <div className="take-it-details">
          <div className="take-it-score">
            <span className="score-value">{combinedScore}</span>
          </div>
          <div className="take-it-breakdown">
            <span>C:{Math.round(confidence)}</span>
            <span className="separator">+</span>
            <span>CF:{confluence >= 0 ? '+' : ''}{Math.round(confluence)}</span>
          </div>
        </div>

        <div className="take-it-countdown">
          {countdown}s
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TakeItBanner;
